// src/pages/admin/AdminUsersManagement.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FiUser, FiSearch, FiMail, FiShield, FiLock, FiUnlock,
  FiTrash2, FiEye, FiUserCheck, FiUserX, FiCalendar,
  FiActivity, FiTrendingUp, FiUsers, FiDownload, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const safeDate = (d) => {
  try {
    if (!d) return null;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
  } catch {
    return null;
  }
};

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const readTotalFromResponse = (res) => {
  try {
    const payload = res?.data ?? {};
    if (payload.totalItems ?? payload.total) {
      return Number(payload.totalItems ?? payload.total);
    }
    if (payload.meta && (payload.meta.total ?? payload.meta.totalItems)) {
      return Number(payload.meta.total ?? payload.meta.totalItems);
    }
    if (
      payload.pagination &&
      (payload.pagination.total ?? payload.pagination.totalItems)
    ) {
      return Number(
        payload.pagination.total ?? payload.pagination.totalItems
      );
    }
    if (payload.data && (payload.data.total ?? payload.data.totalItems)) {
      return Number(payload.data.total ?? payload.data.totalItems);
    }
    const headerTotal =
      res?.headers?.['x-total-count'] || res?.headers?.['X-Total-Count'];
    if (headerTotal) return Number(headerTotal);
  } catch (e) {}
  return 0;
};

const PAGE_DEFAULT = 1;
const LIMIT_DEFAULT = 12;

const normalizeFromServer = (role) => {
  if (!role) return 'user';
  const lower = String(role).toLowerCase();
  if (lower === 'customer') return 'user';
  return lower;
};

const normalizeToServer = (role) => {
  if (!role) return 'customer';
  const lower = String(role).toLowerCase();
  if (lower === 'user') return 'customer';
  return lower;
};

const AdminUsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounced(searchTerm.trim(), 350);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [page, setPage] = useState(PAGE_DEFAULT);
  const [limit, setLimit] = useState(LIMIT_DEFAULT);
  const [total, setTotal] = useState(0);

  const [actionLoading, setActionLoading] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const actionInProgressRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort();
      } catch (e) {}
    };
  }, []);

  const tryGet = async (url, signal, params = {}) => {
    try {
      const res = await API.get(url, { signal, params });
      return { ok: true, res };
    } catch (err) {
      const isAbort =
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED' ||
        err?.message === 'canceled';
      if (isAbort) throw err;
      return { ok: false, err };
    }
  };

  const extractListFromResponse = (r) => {
    const obj = r?.data ?? r ?? {};
    const payload = obj?.data ?? obj;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.users)) return payload.users;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const fetchPage = useCallback(
    async (
      p = page,
      l = limit,
      q = debouncedSearch,
      role = roleFilter,
      status = statusFilter
    ) => {
      if (actionInProgressRef.current) {
        return;
      }

      if (mountedRef.current) setLoading(true);
      try {
        try {
          abortRef.current?.abort();
        } catch (e) {}
        abortRef.current = new AbortController();
        const signal = abortRef.current.signal;

        const params = { page: p, limit: l };
        if (q) params.q = q;
        if (role && role !== 'all')
          params.role = role === 'user' ? 'customer' : role;
        if (status && status !== 'all') params.status = status;

        const endpoints = ['/admin/users'];
        let response = null;
        for (const ep of endpoints) {
          const r = await tryGet(ep, signal, params);
          if (r.ok) {
            response = r.res;
            break;
          }
        }

        if (!response) {
          if (mountedRef.current) {
            setUsers([]);
            setTotal(0);
          }
          return;
        }

        const list = extractListFromResponse(response);

        const normalized = (list || []).map((u) => {
          const copy = { ...u };
          copy.role = normalizeFromServer(copy.role);
          return copy;
        });

        if (mountedRef.current) {
          setUsers(normalized);
          const t = readTotalFromResponse(response);
          setTotal(t || normalized.length || 0);

          const payload = response?.data ?? {};
          const serverPage =
            payload.currentPage ??
            payload.page ??
            payload.data?.currentPage ??
            null;
          const serverLimit =
            payload.itemsPerPage ??
            payload.limit ??
            payload.data?.itemsPerPage ??
            null;
          if (serverPage) setPage(Number(serverPage));
          if (serverLimit) setLimit(Number(serverLimit));
        }
      } catch (err) {
        if (
          err?.name === 'AbortError' ||
          err?.code === 'ERR_CANCELED' ||
          err?.message === 'canceled'
        )
          return;
        if (mountedRef.current) {
          setUsers([]);
          setTotal(0);
          toast.error('Erreur lors du chargement des utilisateurs');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [page, limit, debouncedSearch, roleFilter, statusFilter]
  );

  useEffect(() => {
    fetchPage(page, limit, debouncedSearch, roleFilter, statusFilter);
  }, [fetchPage, page, limit, debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage(1, limit, debouncedSearch, roleFilter, statusFilter);
    }, 100);
    return () => clearTimeout(searchDebounceRef.current);
  }, [debouncedSearch, roleFilter, statusFilter, fetchPage, limit]);

  const setUserLocal = (userId, patch) => {
    setUsers((prev) =>
      prev.map((u) => (u._id === userId ? { ...u, ...patch } : u))
    );
    setSelectedUser((prev) => {
      if (!prev) return prev;
      if (prev._id === userId) {
        return { ...prev, ...patch };
      }
      return prev;
    });
  };

  const withActionLock = async (userId, fn) => {
    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    actionInProgressRef.current = true;
    try {
      await fn();
    } finally {
      setActionLoading((prev) => {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      });
      actionInProgressRef.current = false;
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const prev = users.find((u) => u._id === userId);
    const newStatus =
      currentStatus === 'active' || !currentStatus ? 'suspended' : 'active';
    setUserLocal(userId, { status: newStatus });

    await withActionLock(userId, async () => {
      try {
        const res = await API.patch(`/admin/users/${userId}/status`, {
          status: newStatus
        });
        if (!res?.data?.success)
          throw new Error(res?.data?.message || 'Erreur serveur');

        const returned = res.data?.data ?? null;
        if (returned) {
          const normalizedRole = normalizeFromServer(returned.role);
          setUserLocal(userId, { ...returned, role: normalizedRole });
          setSelectedUser((prevSelected) =>
            prevSelected && prevSelected._id === userId
              ? { ...prevSelected, ...returned, role: normalizedRole }
              : prevSelected
          );
        } else {
          setUserLocal(userId, { status: newStatus });
        }

        toast.success(
          `Utilisateur ${newStatus === 'active' ? 'activé' : 'suspendu'}`
        );

        setTimeout(
          () => fetchPage(page, limit, debouncedSearch, roleFilter, statusFilter),
          300
        );
      } catch (err) {
        setUserLocal(userId, { status: prev?.status });
        toast.error('Impossible de mettre à jour le statut');
      }
    });
  };

  const handleRoleChange = async (userId, newRoleUI) => {
    if (
      !window.confirm(
        `Changer le rôle de cet utilisateur en "${newRoleUI}" ?`
      )
    )
      return;
    const prev = users.find((u) => u._id === userId);
    setUserLocal(userId, { role: newRoleUI });

    await withActionLock(userId, async () => {
      try {
        const roleToSend = normalizeToServer(newRoleUI);

        const res = await API.patch(`/admin/users/${userId}/role`, {
          role: roleToSend
        });

        if (!res?.data?.success) {
          throw new Error(res?.data?.message || 'Erreur serveur');
        }

        const returned = res.data?.data ?? null;
        if (returned) {
          const normalizedRole = normalizeFromServer(returned.role);
          setUserLocal(userId, { ...returned, role: normalizedRole });
          setSelectedUser((prevSelected) =>
            prevSelected && prevSelected._id === userId
              ? { ...prevSelected, ...returned, role: normalizedRole }
              : prevSelected
          );
        } else {
          setUserLocal(userId, { role: newRoleUI });
        }

        toast.success('Rôle mis à jour avec succès');

        setTimeout(
          () => fetchPage(page, limit, debouncedSearch, roleFilter, statusFilter),
          300
        );
      } catch (err) {
        setUserLocal(userId, { role: prev?.role });
        setSelectedUser((prevSelected) =>
          prevSelected && prevSelected._id === userId
            ? { ...prevSelected, role: prev?.role }
            : prevSelected
        );
        toast.error(
          err?.response?.data?.message ||
            'Erreur lors de la mise à jour du rôle'
        );
      }
    });
  };

  const handleDeleteUser = async (userId) => {
    if (
      !window.confirm(
        'Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.'
      )
    )
      return;
    const prevUsers = users;
    setUsers((prev) => prev.filter((u) => u._id !== userId));
    if (selectedUser && selectedUser._id === userId) {
      setSelectedUser(null);
      setShowDetailsModal(false);
    }
    try {
      const res = await API.delete(`/admin/users/${userId}`);
      if (!res?.data?.success)
        throw new Error(res?.data?.message || 'Erreur serveur');
      toast.success('Utilisateur supprimé');
      fetchPage(page, limit, debouncedSearch, roleFilter, statusFilter);
    } catch (err) {
      setUsers(prevUsers);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExportCSV = () => {
    if (!users.length) {
      toast.info('Aucun utilisateur à exporter');
      return;
    }
    const rows = [
      ['_id', 'Nom', 'Email', 'Rôle', 'Statut', 'Inscrit le'],
      ...users.map((u) => [
        u._id || '',
        `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        u.email || '',
        u.role || 'user',
        u.status || 'active',
        safeDate(u.createdAt)
          ? safeDate(u.createdAt).toLocaleString('fr-FR')
          : ''
      ])
    ];
    const csv = rows
      .map((r) =>
        r
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');
    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Export CSV généré');
  };

  const stats = useMemo(() => {
    const totalItems = total || users.length;
    return {
      total: totalItems,
      active: users.filter(
        (u) => !u.status || u.status === 'active'
      ).length,
      admins: users.filter((u) => u.role === 'admin').length,
      merchants: users.filter((u) => u.role === 'merchant').length,
      regularUsers: users.filter(
        (u) =>
          !u.role || u.role === 'user' || u.role === 'customer'
      ).length,
      newThisMonth: users.filter((u) => {
        if (!u.createdAt) return false;
        const d = new Date(u.createdAt);
        const now = new Date();
        return (
          d.getMonth() === now.getMonth() &&
          d.getFullYear() === now.getFullYear()
        );
      }).length
    };
  }, [users, total]);

  const totalPages = Math.max(
    1,
    Math.ceil((total || users.length) / (limit || 1))
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion des Utilisateurs
            </h1>
            <p className="text-gray-600 mt-1">
              Gérez tous les utilisateurs de la plateforme
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <FiDownload className="mr-2" />
              Exporter CSV
            </button>
            <button
              onClick={() =>
                fetchPage(page, limit, debouncedSearch, roleFilter, statusFilter)
              }
              className="px-4 py-2 border rounded-lg"
            >
              Rafraîchir
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">Tous les rôles</option>
              <option value="admin">Administrateurs</option>
              <option value="merchant">Marchands</option>
              <option value="user">Utilisateurs</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="suspended">Suspendus</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <StatCard
          title="Total"
          value={stats.total}
          icon={<FiUsers className="text-blue-500 text-2xl" />}
        />
        <StatCard
          title="Actifs"
          value={stats.active}
          icon={<FiUserCheck className="text-green-500 text-2xl" />}
        />
        <StatCard
          title="Nouveaux (ce mois)"
          value={stats.newThisMonth}
          icon={<FiTrendingUp className="text-purple-500 text-2xl" />}
        />
        <StatCard
          title="Admins"
          value={stats.admins}
          icon={<FiShield className="text-purple-500 text-2xl" />}
        />
        <StatCard
          title="Marchands"
          value={stats.merchants}
          icon={<FiActivity className="text-blue-500 text-2xl" />}
        />
        <StatCard
          title="Utilisateurs"
          value={stats.regularUsers}
          icon={<FiUser className="text-gray-500 text-2xl" />}
        />
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscription
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {users.length > 0 ? (
                users.map((user, index) => (
                  <tr
                    key={user._id || user.email || index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-semibold">
                            {(
                              (user.firstName?.charAt(0) || '') +
                              (user.lastName?.charAt(0) || '')
                            ).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName || '—'} {user.lastName || ''}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <FiMail className="mr-2 text-gray-400" />
                        {user.email || '—'}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {renderRoleBadge(user.role)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderStatusBadge(user.status)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <FiCalendar className="mr-2" />
                        {safeDate(user.createdAt)
                          ? safeDate(user.createdAt).toLocaleDateString('fr-FR')
                          : '—'}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Voir détails"
                          aria-label={`Voir détails ${user.firstName || user.email || ''}`}
                        >
                          <FiEye size={18} />
                        </button>

                        <button
                          onClick={() => handleStatusToggle(user._id, user.status)}
                          className={
                            actionLoading[user._id]
                              ? 'opacity-60 cursor-wait'
                              : !user.status || user.status === 'active'
                              ? 'text-red-600 hover:text-red-900'
                              : 'text-green-600 hover:text-green-900'
                          }
                          title={
                            !user.status || user.status === 'active'
                              ? 'Suspendre'
                              : 'Activer'
                          }
                          disabled={!!actionLoading[user._id]}
                          aria-disabled={!!actionLoading[user._id]}
                          aria-label={
                            !user.status || user.status === 'active'
                              ? `Suspendre ${user.firstName || user.email || ''}`
                              : `Activer ${user.firstName || user.email || ''}`
                          }
                        >
                          {!user.status || user.status === 'active' ? (
                            <FiLock size={18} />
                          ) : (
                            <FiUnlock size={18} />
                          )}
                        </button>

                        <button
                          onClick={() => handleDeleteUser(user._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                          disabled={!!actionLoading[user._id]}
                          aria-label={`Supprimer ${user.firstName || user.email || ''}`}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <FiUsers className="mx-auto text-4xl mb-2" />
                    <p>Aucun utilisateur trouvé</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {total || users.length} résultat(s) • page {page} / {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            <FiChevronLeft />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            <FiChevronRight />
          </button>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="ml-4 border px-2 py-1 rounded"
          >
            {[6, 12, 24, 48].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">
                  Détails de l&apos;utilisateur
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">
                    Informations personnelles
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-600">Nom complet:</span>{' '}
                      <span className="font-medium">
                        {selectedUser.firstName || '—'}{' '}
                        {selectedUser.lastName || ''}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-600">Email:</span>{' '}
                      {selectedUser.email || '—'}
                    </p>
                    <p>
                      <span className="text-gray-600">Rôle:</span>{' '}
                      {renderRoleBadge(selectedUser.role || 'user')}
                    </p>
                    <p>
                      <span className="text-gray-600">Statut:</span>{' '}
                      {renderStatusBadge(selectedUser.status)}
                    </p>
                    <p>
                      <span className="text-gray-600">Inscrit le:</span>{' '}
                      {safeDate(selectedUser.createdAt)
                        ? safeDate(
                            selectedUser.createdAt
                          ).toLocaleDateString('fr-FR')
                        : '—'}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Changer le rôle</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleRoleChange(selectedUser._id, 'user')}
                      disabled={
                        selectedUser.role === 'user' ||
                        actionLoading[selectedUser._id]
                      }
                      className="px-4 py-2 bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                    >
                      Utilisateur
                    </button>
                    <button
                      onClick={() =>
                        handleRoleChange(selectedUser._id, 'merchant')
                      }
                      disabled={
                        selectedUser.role === 'merchant' ||
                        actionLoading[selectedUser._id]
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
                    >
                      Marchand
                    </button>
                    <button
                      onClick={() =>
                        handleRoleChange(selectedUser._id, 'admin')
                      }
                      disabled={
                        selectedUser.role === 'admin' ||
                        actionLoading[selectedUser._id]
                      }
                      className="px-4 py-2 bg-purple-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700"
                    >
                      Admin
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      {icon}
    </div>
  </div>
);

const renderRoleBadge = (role) => {
  const normalized = role === 'customer' ? 'user' : role || 'user';
  const styles = {
    admin: 'bg-purple-100 text-purple-800',
    merchant: 'bg-blue-100 text-blue-800',
    user: 'bg-gray-100 text-gray-800'
  };
  const labels = {
    admin: 'Administrateur',
    merchant: 'Marchand',
    user: 'Utilisateur'
  };
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        styles[normalized] || styles.user
      }`}
    >
      {labels[normalized] || normalized}
    </span>
  );
};

const renderStatusBadge = (status) => {
  const isActive = status === 'active' || !status;
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {isActive ? 'Actif' : 'Suspendu'}
    </span>
  );
};

export default AdminUsersManagement;
